// Fire-and-forget anonymous telemetry. NEVER awaits — UI must not block on
// analytics. Failures are swallowed (network drop, ad-blocker rejection,
// rate limit). The endpoint accepts only an allowlist of event types; do
// NOT add ad-hoc strings here, extend the backend ANON_EVENT_TYPES tuple
// first.
//
// session_id: stored in sessionStorage so we can group an anonymous visit
// into a funnel without setting a cookie (cookie banner avoidance is the
// week-1 GDPR-friendly stance).

import { getAccessToken } from '@/lib/auth';
import { v4 as uuidv4 } from 'uuid';

export type AnonEventType =
  | 'homepage.viewed'
  | 'marketing_attribution.viewed'
  | 'compare.viewed'
  | 'signup.started'
  | 'sandbox.discovery.invoked'
  | 'page.viewed';

const SESSION_KEY = 'ch.anon_session_id';

// First-touch UTM attribution. Captured once per session from the first URL
// that carries any utm_* param, then replayed on every page.viewed so the
// backend can attribute the whole session to its acquisition source — even
// for pages the user reaches after the querystring is gone.
const UTM_KEY = 'ch.first_touch_utm';
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign'] as const;
type UtmParams = Partial<Record<(typeof UTM_PARAMS)[number], string>>;

// Read utm_* from the current URL and, if any are present AND nothing is
// stored yet, persist them as first-touch. Idempotent; safe to call on every
// load. Returns the stored first-touch UTM (existing or just-captured).
export function captureFirstTouchUtm(): UtmParams {
  if (typeof window === 'undefined') return {};
  try {
    const existing = window.sessionStorage.getItem(UTM_KEY);
    if (existing) return JSON.parse(existing) as UtmParams;

    const search = new URLSearchParams(window.location.search);
    const captured: UtmParams = {};
    for (const key of UTM_PARAMS) {
      const val = search.get(key);
      if (val) captured[key] = val;
    }
    if (Object.keys(captured).length > 0) {
      window.sessionStorage.setItem(UTM_KEY, JSON.stringify(captured));
    }
    return captured;
  } catch {
    return {};
  }
}

function getFirstTouchUtm(): UtmParams {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(UTM_KEY);
    return raw ? (JSON.parse(raw) as UtmParams) : {};
  } catch {
    return {};
  }
}

function getSessionId(): string {
  if (typeof window === 'undefined') return uuidv4();
  try {
    let sid = window.sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = uuidv4();
      window.sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    // Storage disabled (incognito strict mode etc) — fall back to per-call uuid.
    return uuidv4();
  }
}

export function trackAnon(
  eventType: AnonEventType,
  properties?: Record<string, unknown>,
): void {
  // We can't reuse the axios `api` instance — it auto-attaches Bearer and
  // would refresh on 401, neither of which applies here. Use bare fetch.
  // ``keepalive: true`` lets the request survive a tab close so a final
  // homepage.viewed isn't dropped.
  if (typeof fetch === 'undefined') return;

  const base = (import.meta.env.VITE_API_URL || '/v1').replace(/\/$/, '');
  const url = `${base}/telemetry/anon`;
  // The backend persists utm_* into dedicated columns from TOP-LEVEL body
  // fields (AnonEventIn), so lift them out of properties to the body root.
  // They stay in properties too (harmless) for any free-form consumer.
  const p = properties ?? undefined;
  const body = JSON.stringify({
    event_type: eventType,
    session_id: getSessionId(),
    properties: p,
    path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
    utm_source: (p?.utm_source as string | undefined) || undefined,
    utm_medium: (p?.utm_medium as string | undefined) || undefined,
    utm_campaign: (p?.utm_campaign as string | undefined) || undefined,
  });

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  // When signed in, send the bearer so the backend can attribute server-side
  // (it trusts the verified token, never a client-supplied user id). Anonymous
  // when there is no token.
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    void fetch(url, {
      method: 'POST',
      headers,
      body,
      keepalive: true,
    }).catch(() => {
      /* swallow — telemetry is never user-visible */
    });
  } catch {
    // ditto
  }
}

// Convenience wrapper for route-change pageviews. Merges first-touch UTM into
// the event properties and forwards the navigation path. `firstLoad` controls
// whether document.referrer is meaningful (only the very first pageview of a
// session has an external referrer).
export function trackPageview(path: string, opts?: { firstLoad?: boolean }): void {
  const properties: Record<string, unknown> = { path, ...getFirstTouchUtm() };
  if (opts?.firstLoad && typeof document !== 'undefined' && document.referrer) {
    properties.referrer = document.referrer;
  }
  trackAnon('page.viewed', properties);
}
